// @ts-check

/**
 * Custom element for managing collection options on a product page.
 *
 * @class
 * @extends HTMLElement
 */
class ProductCollectionOptionsCustom extends HTMLElement {
  /**
   * Constructs the custom element and initializes properties.
   * @constructor
   */
  constructor() {
    super()

    /** @type {Boolean} */
    this.enabled = this.dataset.enabled === 'true'

    /** @type {HTMLSelectElement} */
    this.selectElement = /** @type {HTMLSelectElement} */ (this.querySelector('select'))

    /** @type {HTMLElement | null} */
    this.variantSelectsCustom = null

    /** @type {ProductFormCustom | null} */
    this.productFormCustom = null

    /** @type {HTMLFormElement} */
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'))

    /** @type {number} */
    this.variantPrice = 0

    /** Initialize Slim Select instance */
    this.slimSelect = null
  }

  /**
   * Called when the element is connected to the DOM.
   * Initializes static elements and event listeners.
   */
  connectedCallback() {
    this.setStaticElements()
    this.setSelectListeners()
    this.initializeSlimSelect()
  }

  /**
   * Updates the availability state.
   */
  updateAvailability() {
    const enabled = this.enabled ? 'true' : 'false'
    this.dataset.enabled = enabled
  }

  /**
   * Enables the component.
   */
  enable() {
    this.enabled = true
    this.updateAvailability()
  }

  /**
   * Disables the component.
   */
  disable() {
    this.enabled = false
    this.updateAvailability()
  }

  /**
   * Sets references to static elements used within the component.
   */
  setStaticElements() {
    this.variantSelectsCustom = this.closest('variant-selects-custom')
    if (this.variantSelectsCustom && 'productFormCustom' in this.variantSelectsCustom) {
      /** @type {ProductFormCustom} */
      this.productFormCustom = /** @type {ProductFormCustom} */ (this.variantSelectsCustom.productFormCustom)
    }
  }

  /**
   * Initializes Slim Select on the select element.
   */
  initializeSlimSelect() {
    if (typeof SlimSelect !== 'undefined') {
      this.slimSelect = new SlimSelect({
        select: this.selectElement,
        settings: {
          showSearch: false,
        },
      })
    }
  }

  /**
   * Sets up event listeners for the select element.
   */
  setSelectListeners() {
    this.selectElement.addEventListener('change', this.handleSelectChange.bind(this))
  }

  /**
   * Retrieves the price of the currently selected variant.
   *
   * @returns {number} - The price of the selected variant.
   */
  getVariantPrice() {
    const priceString = this.selectElement.options[this.selectElement.selectedIndex].dataset.price
    return parseFloat(priceString) || 0
  }

  /**
   * Sets the variant price attribute on the form element.
   */
  setVariantPrice() {
    this.variantPrice = this.getVariantPrice()
    this.form.setAttribute('data-variant-price', this.variantPrice.toString())
  }

  /**
   * Handles the change event for the select element.
   */
  handleSelectChange() {
    if (this.productFormCustom) {
      this.productFormCustom.updateSubTotal()
    }
    this.setVariantPrice()
  }
}

customElements.define('product-collection-options-custom', ProductCollectionOptionsCustom)
